import { useState } from 'react';
import { useCupons, useCriarCupom, useBulkDeletarCupons, useDeletarCupom } from '@/hooks/useCupons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Plus, Trash2, CheckSquare, Square, Trash } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { Cupom } from '@/types';

export function GerenciarCupons() {
  const { data: cupons, isLoading } = useCupons();
  const criarCupom = useCriarCupom();
  const deletarCupom = useDeletarCupom();
  const bulkDeletarCupom = useBulkDeletarCupons();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    codigo: '',
    tipo: 'PERCENTUAL',
    valor: '',
    valorMinimo: '0',
    validoDe: '',
    validoAte: '',
    limiteUsos: '0',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanNumber = (val: string | number) => {
      if (typeof val === 'string') {
        const cleaned = val.replace('.', '').replace(',', '.');
        return parseFloat(cleaned) || 0;
      }
      return val;
    };

    await criarCupom.mutateAsync({
      ...formData,
      valor: cleanNumber(formData.valor),
      valorMinimo: cleanNumber(formData.valorMinimo),
      limiteUsos: cleanNumber(formData.limiteUsos),
    });
    setOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este cupom?')) {
      await deletarCupom.mutateAsync(id);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === (cupons?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(cupons?.map((p: any) => p.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Deseja excluir os ${selectedIds.length} cupons selecionados?`)) {
      bulkDeletarCupom.mutate(selectedIds, {
        onSuccess: (response: any) => {
          setSelectedIds([]);
          alert(response?.data?.message || 'Exclusão em massa concluída!');
        },
        onError: () => {
          alert('Erro ao realizar exclusão em massa');
        }
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cupons</h1>
          <p className="text-gray-500">Gerencie cupons de desconto</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button 
              variant="outline" 
              className="text-red-600 border-red-200 hover:bg-red-50 transition-colors"
              onClick={handleBulkDelete}
              disabled={bulkDeletarCupom.isPending}
            >
              <Trash className="h-4 w-4 mr-2" />
              Excluir ({selectedIds.length})
            </Button>
          )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="nuvem" leftIcon={<Plus className="h-4 w-4" />}>
              Novo Cupom
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Criar Cupom</DialogTitle>
              <DialogDescription>
                Configure um novo código de desconto para seus clientes.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Código"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                required
                placeholder="BLACKFRIDAY25"
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Tipo</label>
                <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENTUAL">Percentual (%)</SelectItem>
                    <SelectItem value="VALOR_FIXO">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                label="Valor"
                type="number"
                step="0.01"
                value={formData.valor}
                onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
                required
              />
              <Input
                label="Valor Mínimo do Pedido"
                type="number"
                step="0.01"
                value={formData.valorMinimo}
                onChange={(e) => setFormData({ ...formData, valorMinimo: e.target.value })}
              />
              <Input
                label="Válido de"
                type="datetime-local"
                value={formData.validoDe}
                onChange={(e) => setFormData({ ...formData, validoDe: e.target.value })}
                required
              />
              <Input
                label="Válido até"
                type="datetime-local"
                value={formData.validoAte}
                onChange={(e) => setFormData({ ...formData, validoAte: e.target.value })}
                required
              />
              <Input
                label="Limite de Usos (0 = ilimitado)"
                type="number"
                value={formData.limiteUsos}
                onChange={(e) => setFormData({ ...formData, limiteUsos: e.target.value })}
              />
              <Button type="submit" variant="nuvem" className="w-full" isLoading={criarCupom.isPending}>
                Criar Cupom
              </Button>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-4 px-4 w-10">
                    <button onClick={toggleSelectAll} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                      {selectedIds.length === (cupons?.length || 0) && selectedIds.length > 0 ? (
                        <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                      ) : (
                        <Square className="h-5 w-5" />
                      )}
                    </button>
                  </th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Código</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Tipo</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Valor</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Usos</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Válido até</th>
                  <th className="py-4 px-6 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="py-4 px-6 text-right text-sm font-medium text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody>
                {cupons?.map((cupom: Cupom) => (
                  <tr key={cupom.id} className={`border-b border-gray-200/50 hover:bg-gray-50 transition-colors ${selectedIds.includes(cupom.id) ? 'bg-orange-50/30' : ''}`}>
                    <td className="py-4 px-4">
                      <button onClick={() => toggleSelect(cupom.id)} className="text-gray-400 hover:text-orange-600 transition-colors focus:outline-none">
                        {selectedIds.includes(cupom.id) ? (
                          <CheckSquare className="h-5 w-5 text-[#ED7347]" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </td>
                    <td className="py-4 px-6 font-mono text-lg text-primary">{cupom.codigo}</td>
                    <td className="py-4 px-6 text-gray-500">
                      {cupom.tipo === 'PERCENTUAL' ? 'Percentual' : 'Valor Fixo'}
                    </td>
                    <td className="py-4 px-6 text-gray-900">
                      {cupom.tipo === 'PERCENTUAL' ? `${cupom.valor}%` : formatCurrency(cupom.valor)}
                    </td>
                    <td className="py-4 px-6 text-gray-900">
                      {cupom.usosAtuais}
                      {cupom.limiteUsos > 0 && ` / ${cupom.limiteUsos}`}
                    </td>
                    <td className="py-4 px-6 text-gray-500">
                      {formatDate(cupom.validoAte)}
                    </td>
                    <td className="py-4 px-6">
                      <Badge variant={cupom.ativo ? 'success' : 'secondary'}>
                        {cupom.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(cupom.id)}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
